'use client';

import { use } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { Beef, Dumbbell, ArrowRight, Scale } from 'lucide-react';

export default function ProteinasPage({ params: paramsPromise }) {
  const { locale } = use(paramsPromise);
  const t = useTranslations('ProteinasMasaMuscular');
  
  return (
    <div className="min-h-screen bg-[#020617] text-white">
      <div className="bg-gradient-to-br from-cyan-950 via-[#020617] to-blue-950 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-black mb-6 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-600 bg-clip-text text-transparent">
            {t('pageTitle')}
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            {t('pageSubtitle')}
          </p>
          <Link href={`/${locale}/register`} className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 px-8 py-4 rounded-lg font-semibold shadow-lg shadow-cyan-500/50">
            {t('calculateMacrosButton')} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Scale className="w-8 h-8 text-cyan-500" />
            {t('section1Title')}
          </h2>
          <div className="bg-gradient-to-br from-cyan-950 to-blue-950 border border-cyan-500/30 rounded-lg p-6 mb-6">
            <h3 className="font-bold text-2xl mb-4 text-cyan-400">{t('section1Subtitle')}</h3>
            <div className="space-y-3 text-gray-200">
              <div className="flex items-center gap-3">
                <div className="text-3xl font-black text-cyan-400">1.6-2.2g</div>
                <div>
                  <div className="font-semibold">{t('hypertrophy')}</div>
                  <div className="text-sm text-gray-400">{t('hypertrophyUnit')}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-3xl font-black text-blue-400">1.2-1.6g</div>
                <div>
                  <div className="font-semibold">{t('maintenance')}</div>
                  <div className="text-sm text-gray-400">{t('maintenanceUnit')}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-3xl font-black text-purple-400">2.2-3.0g</div>
                <div>
                  <div className="font-semibold">{t('cutting')}</div>
                  <div className="text-sm text-gray-400">{t('cuttingDesc')}</div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 bg-slate-900 rounded-lg">
              <strong className="text-cyan-400">{t('exampleIntro')}</strong> {t('exampleText', { weight: '70 kg', range: '112-154g' })}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Beef className="w-8 h-8 text-orange-500" />
            {t('section2Title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-900 border border-orange-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-orange-400 mb-4">{t('animalComplete')}</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex justify-between"><span>🍗 {t('chickenBreast')}</span> <strong>30g/100g</strong></li>
                <li className="flex justify-between"><span>🐟 {t('fishSalmonTuna')}</span> <strong>25g/100g</strong></li>
                <li className="flex justify-between"><span>🥚 {t('wholeEgg')}</span> <strong>6g/huevo</strong></li>
                <li className="flex justify-between"><span>🥩 {t('leanBeef')}</span> <strong>26g/100g</strong></li>
                <li className="flex justify-between"><span>🥛 {t('wheyProtein')}</span> <strong>20-25g/scoop</strong></li>
                <li className="flex justify-between"><span>🐟 {t('shrimp')}</span> <strong>24g/100g</strong></li>
              </ul>
            </div>
            <div className="bg-slate-900 border border-green-500/30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-400 mb-4">{t('vegetalSources')}</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex justify-between"><span>🌱 {t('lentils')}</span> <strong>18g/100g</strong></li>
                <li className="flex justify-between"><span>🫘 {t('chickpeas')}</span> <strong>19g/100g</strong></li>
                <li className="flex justify-between"><span>🌾 {t('quinoa')}</span> <strong>14g/100g</strong></li>
                <li className="flex justify-between"><span>🧊 {t('tofu')}</span> <strong>8g/100g</strong></li>
                <li className="flex justify-between"><span>🌱 {t('soyProtein')}</span> <strong>50g/100g</strong></li>
                <li className="flex justify-between"><span>🥜 {t('peanuts')}</span> <strong>26g/100g</strong></li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
            <Dumbbell className="w-8 h-8 text-red-500" />
            {t('section3Title')}
          </h2>
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-orange-950 to-red-950 border border-orange-500/30 rounded-lg p-6">
              <h3 className="font-bold text-xl text-orange-400 mb-2">{t('postWorkoutTitle')}</h3>
              <p className="text-gray-300 mb-3">
                {t('postWorkoutDesc', { amount: '20-40g proteína' })}
              </p>
              <div className="text-sm text-orange-200">
                ✓ {t('postWorkoutTip')}
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-950 to-slate-950 border border-blue-500/30 rounded-lg p-6">
              <h3 className="font-bold text-xl text-blue-400 mb-2">{t('beforeBedTitle')}</h3>
              <p className="text-gray-300 mb-3">
                {t('beforeBedDesc', { amount: '30g' })}
              </p>
              <div className="text-sm text-blue-200">
                ✓ {t('beforeBedTip')}
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-950 to-green-950 border border-cyan-500/30 rounded-lg p-6">
              <h3 className="font-bold text-xl text-cyan-400 mb-2">{t('distributeTitle')}</h3>
              <p className="text-gray-300 mb-3">
                {t('distributeDesc', { meals: '4-6 comidas' })}
              </p>
              <div className="text-sm text-cyan-200">
                ✓ {t('distributeTip')}
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6">{t('faqTitle')}</h2>
          <div className="space-y-4">
            <details className="bg-slate-900 border border-slate-700 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">{t('faq1Question')}</summary>
              <p className="text-gray-300 mt-3">
                {t('faq1Answer')}
              </p>
            </details>
            <details className="bg-slate-900 border border-slate-700 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">{t('faq2Question')}</summary>
              <p className="text-gray-300 mt-3">
                {t('faq2Answer')}
              </p>
            </details>
            <details className="bg-slate-900 border border-slate-700 rounded-lg p-6">
              <summary className="font-semibold text-lg cursor-pointer">{t('faq3Question')}</summary>
              <p className="text-gray-300 mt-3">
                {t('faq3Answer')}
              </p>
            </details>
          </div>
        </section>

        <section className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-xl p-8 text-center">
          <h2 className="text-3xl font-black mb-4">{t('ctaTitle')}</h2>
          <p className="text-lg mb-6 text-white/90">
            {t('ctaSubtitle')}
          </p>
          <Link href={`/${locale}/register`} className="inline-flex items-center gap-2 bg-white text-cyan-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg shadow-lg">
            {t('ctaButton')} <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </div>
    </div>
  );
}



